import { receiptStore } from "../receipts";
import { vendorStore } from "../vendors";
import type {
  Invoice,
  InvoiceStatus,
  InvoiceStore,
  NewInvoiceInput,
} from "./types";

const STORAGE_KEY = "proofly.invoices.v1";
const BYPASS_USER_ID = "00000000-0000-0000-0000-000000000000";

function readFromStorage(): Invoice[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as Partial<Invoice>[]).map((row) => ({
      createdBy: null,
      submittedBy: null,
      submittedAt: null,
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      vendorId: null,
      ...row,
    })) as Invoice[];
  } catch {
    return [];
  }
}

function writeToStorage(invoices: Invoice[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(invoices));
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `i_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function todayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function deriveStatus(input: NewInvoiceInput): InvoiceStatus {
  if (input.status) return input.status;
  if (
    input.direction === "incoming" &&
    input.dueOn &&
    input.dueOn < todayIso()
  ) {
    return "overdue";
  }
  return "pending";
}

export function createLocalStorageInvoiceStore(): InvoiceStore {
  const listeners = new Set<() => void>();

  let snapshot: Invoice[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Invoice[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  return {
    list(): Invoice[] {
      return snapshot;
    },

    get(id: string): Invoice | undefined {
      return snapshot.find((i) => i.id === id);
    },

    async add(input: NewInvoiceInput): Promise<Invoice> {
      const now = new Date().toISOString();
      const id = generateId();
      const status = deriveStatus(input);

      // Wave 9 — auto-link to a vendor using the counterparty label. Same
      // degraded-but-persisted behavior as the expense store.
      let vendorId: string | null = null;
      if (input.counterparty && input.counterparty.trim()) {
        try {
          const vendor = await vendorStore.upsertByName(input.counterparty);
          vendorId = vendor.id;
        } catch {
          vendorId = null;
        }
      }

      const invoice: Invoice = {
        id,
        counterparty: input.counterparty,
        vendorId,
        direction: input.direction,
        amountCents: input.amountCents,
        currency: input.currency ?? "USD",
        issuedOn: input.issuedOn,
        dueOn: input.dueOn ?? null,
        status,
        notes: input.notes ?? null,
        createdBy: BYPASS_USER_ID,
        submittedBy: BYPASS_USER_ID,
        submittedAt: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,
        createdAt: now,
        updatedAt: now,
      };

      commit([invoice, ...snapshot]);

      if (input.file) {
        try {
          await receiptStore.add({
            file: input.file,
            linkedInvoiceId: id,
          });
        } catch (err) {
          throw err;
        }
      }

      return invoice;
    },

    async addMany(inputs: NewInvoiceInput[]): Promise<Invoice[]> {
      if (inputs.length === 0) return [];

      // Wave 9 — resolve vendor ids up front. Serial rather than parallel so
      // repeated rows for the same counterparty don't race the local upsert.
      const vendorIds: Array<string | null> = [];
      for (const input of inputs) {
        if (!input.counterparty || !input.counterparty.trim()) {
          vendorIds.push(null);
          continue;
        }
        try {
          const vendor = await vendorStore.upsertByName(input.counterparty);
          vendorIds.push(vendor.id);
        } catch {
          vendorIds.push(null);
        }
      }

      // Build every Invoice in-memory first so we only commit (and therefore
      // emit) once at the end. Same rationale as the expense store.
      const now = new Date().toISOString();
      const created: Invoice[] = inputs.map((input, idx) => ({
        id: generateId(),
        counterparty: input.counterparty,
        vendorId: vendorIds[idx] ?? null,
        direction: input.direction,
        amountCents: input.amountCents,
        currency: input.currency ?? "USD",
        issuedOn: input.issuedOn,
        dueOn: input.dueOn ?? null,
        status: deriveStatus(input),
        notes: input.notes ?? null,
        createdBy: BYPASS_USER_ID,
        submittedBy: BYPASS_USER_ID,
        submittedAt: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,
        createdAt: now,
        updatedAt: now,
      }));

      // Prepend so newest imports sit at the top, mirroring `add()`.
      commit([...created, ...snapshot]);
      return created;
    },

    async update(
      id: string,
      patch: Partial<Omit<Invoice, "id" | "createdAt" | "updatedAt">>,
    ): Promise<Invoice> {
      const idx = snapshot.findIndex((i) => i.id === id);
      if (idx === -1) throw new Error(`Invoice ${id} not found`);
      const current = snapshot[idx]!;
      const updated: Invoice = {
        ...current,
        ...patch,
        id: current.id,
        createdAt: current.createdAt,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async setStatus(id: string, status: InvoiceStatus): Promise<Invoice> {
      const idx = snapshot.findIndex((i) => i.id === id);
      if (idx === -1) throw new Error(`Invoice ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status === status) return current;
      const updated: Invoice = {
        ...current,
        status,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const next = snapshot.filter((i) => i.id !== id);
      if (next.length !== snapshot.length) commit(next);
    },

    async submit(id: string): Promise<Invoice> {
      const idx = snapshot.findIndex((i) => i.id === id);
      if (idx === -1) throw new Error(`Invoice ${id} not found`);
      const current = snapshot[idx]!;
      if (!["draft", "rejected"].includes(current.status)) {
        throw new Error(
          `Cannot submit invoice in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Invoice = {
        ...current,
        status: "pending",
        submittedBy: current.submittedBy ?? BYPASS_USER_ID,
        submittedAt: now,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async approve(id: string): Promise<Invoice> {
      const idx = snapshot.findIndex((i) => i.id === id);
      if (idx === -1) throw new Error(`Invoice ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status !== "pending") {
        throw new Error(
          `Cannot approve invoice in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Invoice = {
        ...current,
        status: "approved",
        approvedBy: BYPASS_USER_ID,
        approvedAt: now,
        rejectionReason: null,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async reject(id: string, reason: string): Promise<Invoice> {
      const idx = snapshot.findIndex((i) => i.id === id);
      if (idx === -1) throw new Error(`Invoice ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status !== "pending") {
        throw new Error(
          `Cannot reject invoice in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Invoice = {
        ...current,
        status: "rejected",
        approvedBy: BYPASS_USER_ID,
        approvedAt: now,
        rejectionReason: reason,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
